import { DestroyRef, DOCUMENT, inject } from '@angular/core';

import type { Font, Script, Variant } from '@cosmos/types-font';
import { DEFAULT_FONTS } from '@cosmos/util-fonts';

import { extractFontStyles } from './google-fonts/extractFontStyles';
import { getStylesheet } from './google-fonts/fontStylesheet';
import { getFontId } from './ids';
import {
  createStylesheet,
  fillStylesheet,
  stylesheetExists,
} from './stylesheets';

/**
 * Get the Google Fonts stylesheet for the specified font (in the specified scripts and variants,
 * only the characters needed for creating the font previews), add the necessary CSS declarations to
 * apply them and add the fonts' stylesheets to the document head
 */
export async function loadFontPreviews(
  documentRef: Document,
  fontsArray: Font[],
  scripts: Script[],
  variants: Variant[]
): Promise<void> {
  // Only load previews of fonts which don't have a stylesheet (for preview or full font) yet
  const fontsToFetch = fontsArray
    .map((font: Font): string => font.id)
    .filter((fontId): boolean => !stylesheetExists(documentRef, fontId));

  if (!fontsToFetch.length) {
    return;
  }

  // Create stylesheet elements for all fonts which will be fetched (this prevents other font
  // pickers from loading the fonts as well)
  fontsToFetch.forEach((fontId): void =>
    createStylesheet(documentRef, fontId, true)
  );

  // Get Google Fonts stylesheet containing all requested styles
  const response = await getStylesheet(fontsArray, scripts, variants, true);
  // Parse response and assign styles to the corresponding font
  const fontStyles = extractFontStyles(response);

  // Create separate stylesheets for the fonts
  fontsArray.forEach((font): void => {
    // Add stylesheets for fonts which need to be downloaded
    if (fontsToFetch.includes(font.id)) {
      // Make sure response contains styles for the font
      if (!(font.id in fontStyles)) {
        console.error(
          `Missing styles for font "${font.family}" (fontId "${font.id}") in Google Fonts response`
        );
        return;
      }
      // Insert styles into the stylesheet element which was created earlier
      fillStylesheet(documentRef, font.id, fontStyles[font.id]);
    }
  });
}

/** e.g. '700italic' → `italic 700 1em "Family"`, 'regular' → `400 1em "Family"`. */
function variantToFontShorthand(variant: Variant, family: string): string {
  const italic = variant.includes('italic');
  const weight = variant.match(/^\d+/)?.[0] ?? '400';
  return `${italic ? 'italic ' : ''}${weight} 1em "${family}"`;
}

/**
 * Get the Google Fonts stylesheet for the specified font (in the specified scripts and variants),
 * add the necessary CSS declarations to apply it and add the font's stylesheet to the document head
 */
export async function loadActiveFont(
  documentRef: Document,
  font: Font,
  scripts: Script[],
  variants: Variant[]
): Promise<void> {
  // Get Google Fonts stylesheet containing all requested styles
  const fontStyle = await getStylesheet([font], scripts, variants, false);

  // Insert styles into the stylesheet element which was created earlier
  fillStylesheet(documentRef, font.id, fontStyle);

  // `fillStylesheet` replaces this <style> element's content wholesale,
  // which tears down any already-loaded FontFace for this family (from the
  // preview stylesheet) and installs a fresh, unloaded one for the same
  // rule. Nothing afterward reliably exercises it: canvas text can paint
  // correctly (likely served from the CDN's HTTP cache) without ever
  // pushing the new FontFace through its load lifecycle, leaving
  // `document.fonts` reporting it "unloaded" forever. Ask explicitly
  // instead of relying on incidental use to trigger it.
  await Promise.allSettled(
    variants.map((variant) =>
      documentRef.fonts.load(variantToFontShorthand(variant, font.family))
    )
  );
}

/**
 * Returns an empty string when the requested styles are not available — a rejected
 * request resolves with an error page body instead of a stylesheet.
 */
async function fetchFontStyle(
  font: Font,
  variants: Variant[]
): Promise<string> {
  const fontStyle = await getStylesheet(
    [font],
    ['latin'],
    variants,
    false
  ).catch(() => '');
  return fontStyle.includes('@font-face') ? fontStyle : '';
}

/**
 * Fetch and inject the Google Fonts @font-face stylesheet for a single font family.
 * No-ops if the stylesheet is already present. Does NOT trigger the browser to
 * download the font file — call injectLoadFont() for that.
 *
 * Pass `variants` to get more than the regular weight, e.g. `['regular', '600', '700']`.
 */
export async function loadFont(
  documentRef: Document,
  fontFamily: string,
  variants: Variant[] = ['regular']
): Promise<void> {
  const fontId = getFontId(fontFamily);
  if (stylesheetExists(documentRef, fontId)) return;
  // System fonts are provided by the OS — no stylesheet needed.
  if (DEFAULT_FONTS.has(fontFamily.toLowerCase())) return;

  const font = { id: fontId, family: fontFamily } as Font;
  createStylesheet(documentRef, fontId, false);

  let fontStyle = await fetchFontStyle(font, variants);

  // The Google Fonts API rejects the entire request when the family doesn't
  // provide one of the requested weights, so retry weight by weight to keep
  // the ones the family does provide.
  if (!fontStyle && variants.length > 1) {
    const fontStyles = await Promise.all(
      variants.map((variant) => fetchFontStyle(font, [variant]))
    );
    fontStyle = fontStyles.filter(Boolean).join('\n');
  }

  if (!fontStyle) {
    documentRef.getElementById(`font-${fontId}`)?.remove();
    documentRef.dispatchEvent(
      new CustomEvent('font-fetch-error', { detail: { fontFamily } })
    );
    return;
  }

  fillStylesheet(documentRef, fontId, fontStyle);
}

const FONT_ANCHORS_ID = 'cos-font-anchors';

// Reference count across all active injectLoadFont() contexts. The shared
// container is only removed from the DOM when the last context is destroyed.
let _fontAnchorRefCount = 0;

/**
 * Returns a `loadFont` function bound to the current injection context.
 *
 * Manages a single hidden container element that holds one anchor <span> per
 * font family. The spans keep each font "in use" so the browser maintains its
 * loaded/error status — removing them causes the browser to reset status back
 * to 'unloaded'. The container is removed only when the last context that
 * holds a reference to it is destroyed.
 *
 * Must be called in an injection context (constructor, field initializer).
 */
export function injectLoadFont(): (fontFamily: string) => Promise<void> {
  const doc = inject(DOCUMENT);
  const destroyRef = inject(DestroyRef);

  // Get-or-create the shared container for font anchor spans.
  let container = doc.getElementById(FONT_ANCHORS_ID);
  if (!container) {
    container = doc.createElement('div');
    container.id = FONT_ANCHORS_ID;
    container.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;';
    doc.body?.appendChild(container);
  }

  _fontAnchorRefCount++;
  const containerRef = container;
  destroyRef.onDestroy(() => {
    if (--_fontAnchorRefCount === 0) containerRef.remove();
  });

  return async (fontFamily: string) => {
    await loadFont(doc, fontFamily);

    // Add a persistent anchor span for this family if not already present.
    // Browsers only download font files when a rendered element needs them,
    // and reset status to 'unloaded' when no element references the font.
    if (
      !containerRef.querySelector(`[data-font="${CSS.escape(fontFamily)}"]`)
    ) {
      const span = doc.createElement('span');
      span.dataset['font'] = fontFamily;
      span.style.fontFamily = fontFamily;
      span.textContent = 'BESbswy'; // cspell:disable-line
      containerRef.appendChild(span);
    }
  };
}
